import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { AppComponent } from './app.component';
import { LayoutService } from './layout/layout.service';
import { BreadcrumbService } from './services/breadcrumb.service';
import { EventsCtwService } from './services/events-ctw.service';
import { SearchModalComponent } from './shared/search-modal/search-modal.component';

@Component({
  selector: 'app-main',
  templateUrl: './app-main.component.html',
  styleUrls: ['./app-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
/**
 * Composant principal abonné au service LayoutService pour actualiser
 * le footer(Mode classique ou carto)
 */
export class AppMainComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('pageAnnouncer') pageAnnouncer!: ElementRef<HTMLParagraphElement>;
  public isMapDisplayed = false;
  public context!: string;
  public disruptionId!: string;
  sideNavOpened = false;
  public showSkipLinks = true;
  private abonnements = new Subscription();
  public showOnHomepage = false;
  public locale!: string;
  public pageTitle = '';
  private readonly siteName = 'My Astuce';
  /** Mapping des paths de route vers les clés de traduction menu pour les pages sans breadcrumb */
  private readonly routeTitleKeys: Record<string, string> = {
    'itineraires': 'menu.itineraires',
    'carte-interactive': 'menu.carteInteractive',
    'public-transport-hourly': 'menu.horaires',
    'confirmation-email': 'menu.confirmationEmail',
    'mot-de-passe-oublie': 'menu.motDePasseOublie',
  };
  private pageTitleSetByBreadcrumb = false;
  private subscriptionRemover = new Subject<void>();
  constructor(
    public app: AppComponent,
    private layoutService: LayoutService,
    private cd: ChangeDetectorRef,
    public router: Router,
    public localizeRouter: LocalizeRouterService,
    private translate: TranslateService,
    public dialog: MatDialog,
    public eventCtwService: EventsCtwService,
    public route: ActivatedRoute,
    private titleService: Title,
    private breadcrumbService: BreadcrumbService
  ) {
    this.locale = this.translate.currentLang;
    this.translate.onLangChange
      .pipe(takeUntil(this.subscriptionRemover))
      .subscribe(
        (langChangeEvent: LangChangeEvent) =>
          (this.locale = langChangeEvent.lang)
      );

    this.router.events
      .pipe(
        takeUntil(this.subscriptionRemover),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        // Scroller automatiquement en haut lors de toute navigation
        window.scrollTo(0, 0);
        
        this.showOnHomepage = this.router.url === `/${this.locale}`;
        this.handlePageTitleAnnouncementForAccessibility();
      });

    this.breadcrumbService.itemsHandler
      .pipe(takeUntil(this.subscriptionRemover))
      .subscribe((items) => {
        if (items && items.length > 0) {
          const lastItem = items[items.length - 1];
          const pageName = lastItem.label
            ? this.translate.instant(lastItem.label)
            : '';
          this.pageTitleSetByBreadcrumb = true;
          this.updatePageTitle(pageName);
        }
      });

    this.layoutService.showSearchModal().subscribe((value: any) => {
      if (value) {
        const dialogRef = this.dialog.open(SearchModalComponent, {});
        dialogRef.afterClosed().subscribe(() => {
          this.layoutService.setSearchModalDisplayed(false);
        });
      } else {
        this.dialog.closeAll();
      }
    });
    this.route.queryParams.subscribe((params) => {
      this.disruptionId = params.disruptionId;
      this.context = params.context;
    });
  }

  ngOnInit(): void {
    this.abonnements.add(
      this.layoutService.LayoutMap().subscribe((value) => {
        this.isMapDisplayed = value;

        this.cd.detectChanges();
      })
    );
  }
  ngAfterViewInit(): void {}
  
  /**
   * Appelé quand le sidenav s'ouvre
   * Empêche le scroll indésirable sur mobile (iOS Safari)
   */
  onSidenavOpened(): void {
    // Scroller immédiatement en haut pour éviter le scroll automatique sur mobile
    window.scrollTo(0, 0);
  }
  
  /**
   * Appelé quand le sidenav se ferme
   */
  onSidenavClosed(): void {
    // implémenter si nécessaire
  }
  
  /**
   * Gère l'annonce du changement de page pour les lecteurs d'écran (RGAA 7.1 & 8.6).
   * Appelé à chaque NavigationEnd.
   *
   * Stratégie :
   * - Réinitialise le flag breadcrumb
   * - Attend 200ms pour laisser le breadcrumb se déclencher en priorité
   * - Si aucun breadcrumb n'a mis à jour le titre pendant ce délai,
   *   utilise le path de la route comme fallback (pages sans breadcrumb : carto, etc.)
   */
  private handlePageTitleAnnouncementForAccessibility(): void {
    this.pageTitleSetByBreadcrumb = false;

    setTimeout(() => {
      if (!this.pageTitleSetByBreadcrumb) {
        this.updatePageTitle(this.getPageNameFromRoute());
      }
    }, 300);
  }

  /**
   * Extrait le nom de la page à partir du path de la route courante.
   * Utilisé comme fallback quand le breadcrumb n'est pas défini (pages carto, etc.).
   */
  private getPageNameFromRoute(): string {
    // Extraire le segment de route principal (ex: /fr/carte-interactive?foo=bar → carte-interactive)
    const url = this.router.url.split('?')[0]; // retirer les query params
    const segments = url.split('/').filter(Boolean);
    // segments[0] = locale (fr/en), segments[1] = route path
    const routeSegment = segments.length > 1 ? segments[1] : '';

    if (!routeSegment) {
      return this.translate.instant('raccourcis.accueil');
    }

    // Chercher d'abord dans le mapping explicite
    const titleKey = this.routeTitleKeys[routeSegment];
    if (titleKey) {
      const translated = this.translate.instant(titleKey);
      if (translated !== titleKey) {
        return translated;
      }
    }

    // Fallback : tenter ROUTES inversé → chercher la clé ROUTES qui donne ce segment
    const routes = this.translate.instant('ROUTES');
    if (routes && typeof routes === 'object') {
      for (const [key, value] of Object.entries(routes)) {
        if (value === routeSegment) {
          // Tenter menu.{camelCase(key)}
          const camelKey = key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
          const menuTranslation = this.translate.instant(`menu.${camelKey}`);
          if (menuTranslation !== `menu.${camelKey}`) {
            return menuTranslation;
          }
        }
      }
    }

    // Dernier fallback : humaniser le segment de route
    return routeSegment
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  /**
   * Met à jour le titre de la page (balise <title>) et annonce le changement
   * aux lecteurs d'écran via la balise sr-only avec focus.
   * Critères RGAA 7.1 et 8.6
   */
  private updatePageTitle(pageName: string): void {
    const fullTitle = pageName
      ? `${pageName} \u2013 ${this.siteName}`
      : this.siteName;
    this.titleService.setTitle(fullTitle);
    this.pageTitle = fullTitle;

    // Placer le focus sur l'élément sr-only pour que le lecteur d'écran annonce le changement
    setTimeout(() => {
      if (this.pageAnnouncer?.nativeElement) {
        this.pageAnnouncer.nativeElement.focus();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.abonnements.unsubscribe();
    this.showOnHomepage = false;
    this.subscriptionRemover.next();
    this.subscriptionRemover.complete();
  }
  raisedEvent(event: any): void {
    const queryParamsObj: any = {};
    event.detail.parameters.set('context', 'DisruptionDetail');
    event.detail.parameters.forEach((value: boolean, key: string) => {
      queryParamsObj[key] = value;
    });
    this.router.navigate(
      [
        '/',
        this.locale,
        this.translate.instant('ROUTES.public-transport-info'),
      ],
      {
        queryParams: queryParamsObj,
      }
    );
  }

  closeSkipLinks(): void {
    this.showSkipLinks = false;
  }

  onSkipToContent(event: Event): void {
    event.preventDefault();
    const el = document.getElementById('contenu');
    if (el) {
      try {
        (el as HTMLElement).focus();
      } catch {}
    }
    this.closeSkipLinks();
  }
}
